import { AnalyticsService } from 'base/analytics';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { FavIcon } from 'base/ui/fav_icon';
import { IconLock, IconShuffleAi, IconStars } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Grow, Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { Locale, localeSchema, LOCALE_INFO } from 'base/ui/locales';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { SelectButton } from 'base/ui/select';
import { PromptTextarea } from 'base/ui/textarea';
import { LONG_TOAST_DURATION, ToastQueue } from 'base/ui/toast';
import {
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
} from 'base/ui/typography';
import { BlocksService } from 'blocks/blocks_service';
import { currentUrl } from 'blocks/current_url';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { SitemapImportDialog } from 'blocks/sitemap_import/sitemap_import_dialog';
import { validateUrl } from 'blocks/sitemap_import/sitemap_import_entry_dialog';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import { Assistant } from '../ai/assistant';
import { MIN_LLM_INPUT_LENGTH, spaceIsEmpty } from '../ai/page_sections';
import {
  getCompanyDescriptionError,
  LimitCounter,
  PROJECT_BRIEF_LIMIT,
} from '../ai/validation';
import { getGlobalDesignStore } from '../design/design_store';
import {
  HomepageSpaceEditingActions,
  SpaceEditingActions,
} from '../editing_actions/space';
import { EditorMode, editorModeStore } from '../editor_mode';
import { PreferredGenPageNumbers, Project, Space } from '../model';
import { pageAndAllSubpages } from '../model_helpers';
import { selectionLocale, SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import styles from './space.css';
import { FormEmptyState, RequiredStar } from './ui';

export const SpaceGenerateForm = observer(function _SpaceGenerateForm({
  analytics,
  blocksService,
  space,
  spaceEditing,
  assistant,
  selection,
  session,
  flags,
  toastQueue,
}: {
  analytics: AnalyticsService;
  blocksService: BlocksService;
  space: Space | 'home';
  spaceEditing: SpaceEditingActions | HomepageSpaceEditingActions;
  assistant: Assistant;
  selection: SelectionStore;
  session: ProjectSession;
  flags: FeatureFlagsStore;
  toastQueue: ToastQueue;
}) {
  const [importDomainState, setImportDomainState] =
    React.useState<OnSitemapImportChangeOpts>({ kind: 'not-ready' });
  const { project } = selection;
  const inWireframeMode = editorModeStore.get() === EditorMode.Wireframe;
  const prompt = (space === 'home' ? project.brief : space.description) || '';
  const tasks = assistant.taskLog.tasksAffecting(
    space === 'home' ? project.homePage : space.subPages[0],
  );
  const isGenerating = tasks.some((t) => !t.completed);

  if (inWireframeMode && !session.canViewAllWireframes && space !== 'home') {
    return (
      <FormEmptyState
        icon={IconLock}
        buttonLabel="Upgrade"
        onClick={() => session.showUpgradeWall?.('pages')}
      >
        To unlock wireframes for all pages, you need to upgrade.
      </FormEmptyState>
    );
  }

  const preferredNumPages =
    (space === 'home'
      ? project.state.preferredNumPages
      : space.state.preferredNumPages) || DEFAULT_GENERATE_PAGE_COUNT;
  const locale = selectionLocale({ space }, selection.project);
  const promptError = getCompanyDescriptionError(prompt);
  const isSpaceEmpty = spaceIsEmpty(project, space);

  return (
    <Rows spacing={16}>
      <SpaceInfoForm
        prompt={prompt}
        updatePrompt={spaceEditing.setDescription}
        promptError={promptError}
        preferredNumPages={preferredNumPages}
        updatePreferredNumPages={spaceEditing.setPreferredNumOfPages}
        locale={locale}
        updateLocale={spaceEditing.setLocale}
      />
      {/* There are 2 ways to generate a site (from a prompt and by crawling an existing site),
        and one "Generate" button. To avoid confusion the import functionality is only available 
        when the prompt and the space are empty. */}
      {!inWireframeMode && isSpaceEmpty && prompt.length === 0 && (
        <>
          <LabelledDivider label="Or" />
          <SitemapImportForm
            onImportDomainChange={(state) => {
              setImportDomainState(state);
            }}
          />
        </>
      )}
      <DividerLine expandToEdges={true} />
      {!inWireframeMode &&
      isSpaceEmpty &&
      importDomainState.kind === 'ready' ? (
        <SitemapImportButton
          analytics={session.analytics}
          blocksService={blocksService}
          toastQueue={toastQueue}
          assistant={assistant}
          project={project}
          domain={importDomainState.domain}
        />
      ) : (
        <GenerateSpaceButton
          analytics={session.analytics}
          assistant={assistant}
          project={project}
          space={space}
          isSpaceEmpty={isSpaceEmpty}
          prompt={prompt}
          preferredNumPages={preferredNumPages}
          inWireframeMode={inWireframeMode}
          error={promptError}
          isGenerating={isGenerating}
          toastQueue={toastQueue}
        />
      )}

      {editorModeStore.get() === EditorMode.Design && (
        <>
          <DividerLine />
          <Button
            label="Reapply schemes"
            icon={IconShuffleAi}
            {...SECONDARY_AI_VARIANT}
            onClick={() => {
              const concept = getGlobalDesignStore().design.linkedConcept;
              assistant.applyDesignSchemesToPages(
                pageAndAllSubpages(project.homePage).filter(
                  (p) => p !== project.homePage,
                ),
                concept,
              );
            }}
          />
        </>
      )}
    </Rows>
  );
});

type LabelledDividerProps = {
  label: string;
};

function LabelledDivider({ label }: LabelledDividerProps) {
  return (
    <Inline yalign="center">
      <Grow>
        <div className={styles.labelledDividerLine} />
      </Grow>
      <TextTinyLight color="grey">{label}</TextTinyLight>
      <Grow>
        <div className={styles.labelledDividerLine} />
      </Grow>
    </Inline>
  );
}

type SpaceInfoFormProps = {
  prompt: string;
  updatePrompt: (newPrompt: string) => void;
  promptError: string | undefined;
  preferredNumPages: number;
  updatePreferredNumPages: (
    newPreferredNumPages: PreferredGenPageNumbers,
  ) => void;
  locale: Locale;
  updateLocale: (newLocale: Locale) => void;
};
function SpaceInfoForm({
  prompt,
  updatePrompt,
  promptError,
  preferredNumPages,
  updatePreferredNumPages,
  locale,
  updateLocale,
}: SpaceInfoFormProps) {
  // Focus "prompt" textarea whenever the space changes only if the "prompt" is empty
  const promptRef = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => {
    if (promptRef.current == null) {
      return;
    }
    if (prompt === '') {
      promptRef.current.focus();
    }
  }, [prompt]);

  const tryExample = React.useCallback(() => {
    const currentExample = BRIEF_EXAMPLES.indexOf(prompt);
    const nextExample =
      currentExample >= 0
        ? currentExample + 1
        : Math.floor(Math.random() * BRIEF_EXAMPLES.length);
    updatePrompt(BRIEF_EXAMPLES[nextExample % BRIEF_EXAMPLES.length]);
  }, [prompt, updatePrompt]);

  // Don't show the error when the user opens this panel on an empty project
  // or when the prompt is too short.
  // Short prompts aren't valid and the "Generate" button is disabled.
  // For prompts we don't want to show the error because it's too distracting
  // and doesn't add much value.
  const hideError = prompt.length < MIN_LLM_INPUT_LENGTH;

  const numPagesMenu = NUM_PAGES_OPTIONS.map((opt) =>
    menuItem({
      label: opt.label,
      checked: preferredNumPages === opt.value,
      onClick: () => updatePreferredNumPages(opt.value),
    }),
  );
  return (
    <Rows spacing={16}>
      <Rows spacing={8}>
        <div className={styles.labelWithButton}>
          <TextTinyLight color="grey">
            Description
            <RequiredStar />
          </TextTinyLight>
          <TextTinyLight color="grey">
            <Link onClick={tryExample}>Try example</Link>
          </TextTinyLight>
        </div>
        <TextSmallLight>
          <PromptTextarea
            ref={promptRef}
            value={prompt}
            onChange={updatePrompt}
            lang={locale}
            placeholder="Describe the company in a sentence or two to generate a sitemap..."
            minHeight={120}
            allowNewlines
            isInvalid={promptError != null && !hideError}
          />
        </TextSmallLight>
        {promptError && !hideError && (
          <TextTinyLight color="grey">{promptError}</TextTinyLight>
        )}
        <LimitCounter value={prompt} limit={PROJECT_BRIEF_LIMIT} />
      </Rows>

      <Rows spacing={8}>
        <TextTinyLight color="grey">Number of pages</TextTinyLight>
        <SelectButton menu={numPagesMenu} />
      </Rows>

      <Rows spacing={8}>
        <TextTinyLight color="grey">Language</TextTinyLight>
        <LanguageSelect value={locale} onChange={updateLocale} />
      </Rows>
    </Rows>
  );
}

type OnSitemapImportChangeOpts =
  | { kind: 'not-ready' }
  | { kind: 'ready'; domain: string };

type SitemapImportFormProps = {
  onImportDomainChange: (opts: OnSitemapImportChangeOpts) => void;
};

export function SitemapImportForm({
  onImportDomainChange,
}: SitemapImportFormProps) {
  const [url, setUrl] = React.useState<string>('');

  const handleUrlOnChange = React.useCallback(
    (url: string) => {
      setUrl(url);
      const validatedUrl = validateUrl(url);
      if (validatedUrl.valid) {
        onImportDomainChange({
          kind: 'ready',
          domain: validatedUrl.domain,
        });
      } else {
        onImportDomainChange({ kind: 'not-ready' });
      }
    },
    [onImportDomainChange],
  );

  const validatedUrl = validateUrl(url);

  return (
    <Rows spacing={16}>
      <Rows spacing={8}>
        <TextTinyLight color="grey">Import sitemap</TextTinyLight>
        <FormInput
          size="medium"
          type="text"
          value={url}
          placeholder="Enter website URL"
          invalid={url !== '' && !validatedUrl.valid}
          onChange={handleUrlOnChange}
        />
        {validatedUrl.valid && validatedUrl.trailing ? (
          <Inline spacing={4} yalign="center">
            <TextTinyLight color="green">
              {`Import will start from ${validatedUrl.domain}`}
            </TextTinyLight>
          </Inline>
        ) : null}
      </Rows>
    </Rows>
  );
}

type SitemapImportButtonProps = {
  analytics: AnalyticsService;
  blocksService: BlocksService;
  toastQueue: ToastQueue;
  assistant: Assistant;
  project: Project;
  domain: string;
};

function SitemapImportButton({
  analytics,
  blocksService,
  toastQueue,
  assistant,
  project,
  domain,
}: SitemapImportButtonProps) {
  const [showSitemapImportDialog, setShowSitemapImportDialog] =
    React.useState(false);
  return (
    <>
      <Button
        {...PRIMARY_AI_VARIANT}
        label="Generate sitemap"
        icon={IconStars}
        disabled={showSitemapImportDialog}
        onClick={() => {
          setShowSitemapImportDialog(true);
        }}
      />
      {showSitemapImportDialog && (
        <SitemapImportDialog
          analytics={analytics}
          blocksService={blocksService}
          importBehaviour={{
            kind: 'import-into-existing-project',
            toastQueue,
            assistant,
            project,
            domain,
          }}
          onClose={() => setShowSitemapImportDialog(false)}
        />
      )}
    </>
  );
}

type GenerateSpaceButtonProps = {
  analytics: AnalyticsService;
  assistant: Assistant;
  project: Project;
  space: Space | 'home';
  isSpaceEmpty: boolean;
  prompt: string;
  preferredNumPages: PreferredGenPageNumbers;
  inWireframeMode: boolean;
  error: string | undefined;
  isGenerating: boolean;
  toastQueue: ToastQueue;
};

function GenerateSpaceButton({
  analytics,
  assistant,
  project,
  space,
  isSpaceEmpty,
  prompt,
  preferredNumPages,
  inWireframeMode,
  error,
  isGenerating,
  toastQueue,
}: GenerateSpaceButtonProps) {
  const [confirmGenerateDialogOpen, setConfirmGenerateDialogOpen] =
    React.useState(false);

  const generateSpace = React.useCallback(() => {
    if (isGenerating) {
      return;
    }

    const brief =
      (space !== 'home' && space.description) || project.brief || '';

    const isExample = BRIEF_EXAMPLES.indexOf(brief) !== -1;
    analytics.track({
      event: 'project:sitemap_generate',
      generate_page_count: preferredNumPages,
      has_example_brief: isExample,
      has_generated_sitemap: !isSpaceEmpty,
    });

    return assistant.generateSpace(space, {
      pageCount: preferredNumPages,
      generateCopy: inWireframeMode,
      chooseComponents: inWireframeMode,
    });
  }, [
    isGenerating,
    space,
    project,
    assistant,
    preferredNumPages,
    inWireframeMode,
    analytics,
    isSpaceEmpty,
  ]);

  React.useEffect(() => {
    const params = currentUrl.searchParams;
    const generateOnLoad = params.get('generateOnLoad');
    const sitemapImportedFrom = params.get('sitemapImportedFrom');
    params.delete('generateOnLoad');
    params.delete('sitemapImportedFrom');
    if (generateOnLoad || sitemapImportedFrom) {
      window.history.replaceState(
        null,
        '',
        decodeURIComponent(`${currentUrl.pathname}?${params}`),
      );
    }
    if (isSpaceEmpty && prompt && generateOnLoad) {
      generateSpace();
    } else if (sitemapImportedFrom) {
      const allPages = pageAndAllSubpages(project.homePage);
      assistant.generatePages(allPages, {
        generateCopy: false,
        chooseComponents: false,
        replaceExistingComponents: false,
      });
      toastQueue.push({
        label: `Sitemap imported from ${sitemapImportedFrom}`,
        confetti: true,
        customRight: <FavIcon domain={sitemapImportedFrom} size="small" />,
        duration: LONG_TOAST_DURATION,
      });
    }
  }, [prompt, generateSpace, isSpaceEmpty, assistant, project, toastQueue]);

  return (
    <Rows spacing={8}>
      <Button
        label={
          isGenerating
            ? 'Generating...'
            : `Generate ${inWireframeMode ? 'wireframe' : 'sitemap'}`
        }
        icon={IconStars}
        {...PRIMARY_AI_VARIANT}
        disabled={isGenerating || error != null}
        tooltip={error}
        tooltipPlacement="horizontal"
        onClick={() => {
          if (isSpaceEmpty) {
            generateSpace();
          } else {
            setConfirmGenerateDialogOpen(true);
          }
        }}
      />
      {!isSpaceEmpty && !isGenerating && (
        <TextTinyLight align="center" color="grey">
          This will override all page sections and copy
        </TextTinyLight>
      )}
      {confirmGenerateDialogOpen && (
        <ConfirmGenerateDialog
          onClose={() => setConfirmGenerateDialogOpen(false)}
          onConfirm={generateSpace}
        />
      )}
    </Rows>
  );
}

export function ConfirmGenerateDialog({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: () => void;
}) {
  const dialog = (
    <Dialog
      title="This will override everything"
      onClose={onClose}
      buttons={
        <>
          <Button label="Cancel" size="large" onClick={onClose} />
          <Button
            label="Continue"
            variant="primary"
            background="ai"
            size="large"
            onClick={() => {
              onClose();
              onConfirm();
            }}
          />
        </>
      }
    >
      <TextRegularLight color="grey">
        It seems you are attempting to generate a sitemap with a new prompt,
        which will override any manual work you have done so far. Confirm by
        clicking 'Continue'.
      </TextRegularLight>
    </Dialog>
  );
  const portal = createPortal(dialog, document.body);
  return <>{portal}</>;
}

function LanguageSelect({
  value,
  onChange,
}: {
  value: Locale;
  onChange: (v: Locale) => void;
}) {
  const menuItems = localeSchema.options.map((locale) =>
    menuItem({
      label: LOCALE_INFO[locale].name,
      searchSynonyms: LOCALE_INFO[locale].searchSynonyms,
      checked: value === locale,
      onClick: () => onChange(locale),
    }),
  );
  return (
    <SelectButton menu={menuItems} searchPlaceholder="Search languages..." />
  );
}

const BRIEF_EXAMPLES = [
  'Cloudify is a cloud-based accounting software product that lets small-business owners manage their finances from anywhere and integrate with more than 1,000 apps.',
  'Rebank is a digital bank that offers a range of financial services primarily through a mobile app, allowing you to manage your money on-the-go.',
  'Gretta is a boutique Architectural firm based in Los Angeles that focuses on homes as well as smaller commercial and community projects.',
];

export const DEFAULT_GENERATE_PAGE_COUNT: PreferredGenPageNumbers = 5;
const NUM_PAGES_OPTIONS: { label: string; value: PreferredGenPageNumbers }[] = [
  // If you ask for like 1 page it'll give you tonnes.  So we start at 4:
  { label: '1-5', value: 4 },
  { label: '5-10', value: 5 },
  { label: '10-15', value: 10 },
  { label: '15-20', value: 15 },
  { label: '20+', value: 20 },
];

const PRIMARY_AI_VARIANT = { variant: 'primary', background: 'ai' } as const;
const SECONDARY_AI_VARIANT = { variant: 'secondary', iconColor: 'ai' } as const;
